import { CONTAINER_QUERY_BREAKPOINTS, cq } from '~/common/containerQuery'
import { useMsg } from '~/common/localizationUtils'
import { $thm } from '~/common/themeSx'
import { ActionBarFilterTabs } from '~/components/v2/ResultsLayout/ActionBar/ActionBar.FilterTabs'
import { VisibleFieldsButton } from '~/components/v2/ResultsLayout/ActionBar/ActionBar.VisibleFieldsButton'
import { Box, Card, Flex, FlexProps, visuallyHiddenStyle } from '~/design_system'
import { useIsSanaRebrandEnabled } from '~/theme/useIsSanaRebrandEnabled'
import { CheckAll } from './ActionBar.CheckAll'
import { DisplayModeButtons } from './ActionBar.DisplayModeButtons'
import { ActionBarResultCount } from './ActionBar.ResultCount'
import { ActionBarSearch } from './ActionBar.Search'
import { SortBy } from './ActionBar.SortBy'
import { ToggleFiltersButton } from './ActionBar.ToggleFiltersButton'

const hasBothFilterTabsAndSortby = '[data-action-bar]:has([data-action-bar-filter-tabs]):has([data-action-bar-sort]) &'

const hasOneOfFilterTabsOrSortby =
  '[data-action-bar]:not(:has([data-action-bar-filter-tabs])):has([data-action-bar-sort]) &, [data-action-bar]:not(:has([data-action-bar-sort])):has([data-action-bar-filter-tabs]) &'

const hasNoneOfFilterTabsOrSortby =
  '[data-action-bar]:not(:has([data-action-bar-filter-tabs])):not(:has([data-action-bar-sort])) &'

/**
 * The ActionBar for a ResultsLayout.
 *
 * The underlying component is our `Flex` component built on Chakra's `Flex` component.
 */
const InnerActionBar = ({ hideCard = false, ...props }: FlexProps & { hideCard?: boolean }) => {
  const msg = useMsg()
  const isRebrand = useIsSanaRebrandEnabled()
  // data-action-bar belongs on the Card. MainNoSpacing gaps below it; on an inner
  // Box that gap becomes padding and the bulk bar sits flush.
  const content = (
    <Flex
      data-testid="action-bar"
      sx={cq({
        // Container
        gap: 4,
        justify: 'space-between',
        flexWrap: 'wrap',
        alignItems: 'center',

        // Left
        '[data-action-bar-left]': {
          flexGrow: 1,
          justifyContent: 'flex-start',
          flexWrap: 'wrap',
          minW: { '@actionBar/base': 'auto', '@actionBar/[1000px]': '25%' },
          [hasBothFilterTabsAndSortby]: {
            w: { '@actionBar/base': 'full', '@actionBar/5xl': 'auto' }
          },
          [hasOneOfFilterTabsOrSortby]: {
            w: { '@actionBar/base': 'full', '@actionBar/3xl': 'auto' }
          },
          [hasNoneOfFilterTabsOrSortby]: {
            w: { '@actionBar/base': 'full', '@actionBar/xl': 'auto' }
          }
        },

        // Right
        '[data-action-bar-right]': {
          justifyContent: 'flex-end',
          alignItems: 'center',
          flexWrap: 'wrap',
          [hasBothFilterTabsAndSortby]: {
            flexGrow: { '@actionBar/base': 1, '@actionBar/5xl': 0.33 }
          },
          [hasOneOfFilterTabsOrSortby]: {
            flexGrow: { '@actionBar/base': 1, '@actionBar/3xl': 0.5 }
          },
          [hasNoneOfFilterTabsOrSortby]: {
            flexGrow: { '@actionBar/base': 1, '@actionBar/xl': 0 }
          }
        },

        // Search
        '[data-action-bar-search]': {
          flexGrow: 1,
          w: 'min-content',
          [hasBothFilterTabsAndSortby]: {
            minW: { '@actionBar/base': '125px', '@actionBar/5xl': '150px' },
            maxW: { '@actionBar/base': 'full', '@actionBar/5xl': '280px' }
          },
          [hasOneOfFilterTabsOrSortby]: {
            minW: { '@actionBar/base': '125px', '@actionBar/3xl': '150px' },
            maxW: { '@actionBar/base': 'full', '@actionBar/3xl': '280px' }
          },
          [hasNoneOfFilterTabsOrSortby]: {
            minW: { '@actionBar/base': '125px', '@actionBar/xl': '150px' },
            maxW: { '@actionBar/base': 'full', '@actionBar/xl': '280px' }
          }
        },

        // Result count
        '[data-action-bar-result-count]': {
          flexGrow: 0,

          // Visually hide the result count on small action bars
          [`@container actionBar (max-width: ${CONTAINER_QUERY_BREAKPOINTS['3xl']}px)`]: {
            '&': visuallyHiddenStyle
          },
          // Visually hide the result count on medium action bars, but only when filter tabs or sort are present
          [`@container actionBar (max-width: ${CONTAINER_QUERY_BREAKPOINTS['5xl']}px)`]: {
            '[auto-action-bar]:has([data-action-bar-filter-tabs], [data-action-bar-sort]) &': visuallyHiddenStyle
          }
        },

        // Filter tabs
        '[data-action-bar-filter-tabs]': {
          [hasBothFilterTabsAndSortby]: {
            flexGrow: { '@actionBar/base': 0, '@actionBar/5xl': 1 },
            justifyContent: { '@actionBar/base': 'flex-start', '@actionBar/5xl': 'center' }
          },
          [hasOneOfFilterTabsOrSortby]: {
            flexGrow: { '@actionBar/base': 0, '@actionBar/3xl': 1 },
            justifyContent: { '@actionBar/base': 'flex-start', '@actionBar/3xl': 'center' }
          },
          [hasNoneOfFilterTabsOrSortby]: {
            flexGrow: { '@actionBar/base': 0, '@actionBar/xl': 1 },
            justifyContent: { '@actionBar/base': 'flex-start', '@actionBar/xl': 'center' }
          }
        },

        // Sort By
        '[data-action-bar-sort]': {
          [hasBothFilterTabsAndSortby]: {
            flexGrow: { '@actionBar/base': 1, '@actionBar/5xl': 0 }
          },
          [hasOneOfFilterTabsOrSortby]: {
            flexGrow: { '@actionBar/base': 1, '@actionBar/3xl': 0 }
          },
          [hasNoneOfFilterTabsOrSortby]: {
            flexGrow: { '@actionBar/base': 1, '@actionBar/xl': 0 }
          }
        },

        // Display Mode Buttons
        '[data-display-mode-buttons]': {},

        // Visible Fields Button
        '[data-visible-fields-button]': {}
      })}
      {...props}
    />
  )

  return hideCard ? (
    <Box data-action-bar sx={{ containerType: 'inline-size', containerName: 'actionBar' }}>
      {content}
    </Box>
  ) : (
    <Card
      {...(isRebrand ? { variant: 'tonal' } : { bg: 'gray.50' })}
      aria-label={msg('common.action_bar')}
      data-action-bar
      // Legacy: flat bottom so action joins the bulk/list stack.
      {...(!isRebrand && { roundedBottom: 0 })}
      sx={$thm({ containerType: 'inline-size', containerName: 'actionBar', py: 4, px: { $base: 4, $canvas: 6 } })}>
      {content}
    </Card>
  )
}

export const ActionBar = Object.assign(InnerActionBar, {
  Left: ActionBarLeft,
  Right: ActionBarRight,
  ToggleFiltersButton,
  CheckAll,
  Search: ActionBarSearch,
  FilterTabs: ActionBarFilterTabs,
  ResultCount: ActionBarResultCount,
  SortBy,
  DisplayModeButtons,
  VisibleFieldsButton
})

function ActionBarLeft(props: FlexProps) {
  return (
    <Flex
      data-action-bar-left
      data-testid="action-bar-left"
      sx={{
        gap: 4
      }}
      {...props}
    />
  )
}

function ActionBarRight(props: FlexProps) {
  return (
    <Flex
      data-action-bar-right
      data-testid="action-bar-right"
      sx={{
        columnGap: 3,
        rowGap: 4
      }}
      {...props}
    />
  )
}
